import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import FilmStripLoader from '../components/FilmStripLoader';
import { getYouTubeEmbedUrl } from '../utils/youtubeEmbed';

import trial from "../../src/assets/images/trial-bg.png"

const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();

  const [movie, setMovie] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  // const [selectedSeason, setSelectedSeason] = useState(null); // Not used in MovieDetail

  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);

  const [showTrailer, setShowTrailer] = useState(false);

  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoEmbedUrl, setVideoEmbedUrl] = useState('');
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [reviewsSlide, setReviewsSlide] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const [showMobilePlayer, setShowMobilePlayer] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Cast scroll ref (for arrows)
  const castRef = useRef(null);
  const videoIframeRef = useRef(null);
  const videoContainerRef = useRef(null);
  const mobilePlayerRef = useRef(null);
  // const mobilePlayerContainerRef = useRef(null); // Not currently used
  const iframeLoadTimeoutRef = useRef(null);

  // Monitor iframe loading on mobile
  useEffect(() => {
    if (showMobilePlayer && videoEmbedUrl && mobilePlayerRef.current) {
      // Set a timeout to check if iframe loads
      iframeLoadTimeoutRef.current = setTimeout(() => {
        const iframe = mobilePlayerRef.current;
        if (iframe && (!iframe.contentWindow || !iframe.contentDocument)) {
          console.warn('Iframe may not have loaded properly');
          // Try to reload iframe
          const currentSrc = iframe.src;
          if (currentSrc) {
            iframe.src = '';
            setTimeout(() => {
              iframe.src = currentSrc;
            }, 100);
          }
        }
      }, 5000); // 5 second timeout
    }

    return () => {
      if (iframeLoadTimeoutRef.current) {
        clearTimeout(iframeLoadTimeoutRef.current);
      }
    };
  }, [showMobilePlayer, videoEmbedUrl]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // Wait for all API calls to complete before hiding loader
        await Promise.all([
          fetchMovie(),
          fetchReviews()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        // Small delay to ensure everything is rendered before hiding loader
        setTimeout(() => {
          setLoading(false);
          // Start fade-out animation, then hide loader after animation completes
          setTimeout(() => {
            setShowLoader(false);
          }, 600);
        }, 500);
      }
    };
    
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showTrailer || showVideoPlayer || showMobilePlayer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTrailer, showVideoPlayer, showMobilePlayer]);

  const fetchMovie = async () => {
    try {
      const res = await axios.get(`/api/movies/${id}`);
      setMovie(res.data);
    } catch (error) {
      console.error('Error fetching movie:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`/api/reviews/movie/${id}`);
      setReviews(res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/reviews', {
        movieId: id,
        ...reviewForm,
      });
      setReviewForm({ rating: 5, comment: '' });
      setShowReviewForm(false);
      fetchReviews();
    } catch (error) {
      console.error('Error adding review:', error);
    }
  };

  const handlePlayMovie = async () => {
    if (!hasActiveSubscription) {
      navigate('/subscriptions');
      return;
    }

    // Detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    setLoadingVideo(true);
    setVideoError(null);

    try {
      // Get video token
      const tokenRes = await axios.get(`/api/movies/${id}/watch`);
      const token = tokenRes.data.token;

      // Get embed URL from backend (works for both mobile and desktop)
      const embedRes = await axios.get(`/api/stream/${token}`);
      
      if (embedRes.data.success && embedRes.data.embedUrl) {
        if (isMobile) {
          // On mobile, show player in modal (not fullscreen)
          // Use direct Bunny Stream URL (absolute URL, not relative)
          setVideoEmbedUrl(embedRes.data.embedUrl);
          setShowVideoPlayer(true);
        } else {
          // On desktop, open modal
          setShowVideoPlayer(true);
          setVideoEmbedUrl(embedRes.data.embedUrl);
        }
      } else {
        setVideoError('Échec du chargement de la vidéo. Veuillez réessayer.');
        setShowMobilePlayer(false);
        setShowVideoPlayer(false);
      }
    } catch (error) {
      console.error('Error loading video:', error);
      if (error.response?.status === 403) {
        setVideoError('Vous avez besoin d\'un abonnement actif pour regarder ce film.');
      } else {
        setVideoError('Échec du chargement de la vidéo. Veuillez réessayer.');
      }
      setShowMobilePlayer(false);
      setShowVideoPlayer(false);
    } finally {
      setLoadingVideo(false);
    }
  };

  // Removed automatic fullscreen - user can click fullscreen button manually if needed

  // Removed automatic fullscreen - user can click fullscreen button manually

  // Removed fullscreen change handler - no automatic fullscreen behavior

  // Monitor video player health - detect if it stops responding
  useEffect(() => {
    if (!showVideoPlayer || !videoEmbedUrl) return;

    let healthCheckInterval;
    let checkCount = 0;

    const healthCheck = () => {
      const iframe = videoIframeRef.current;
      if (!iframe) return;

      checkCount++;
      
      // Every 10 seconds, check if iframe is still accessible
      try {
        // Just check if iframe exists and has src
        if (iframe.src && iframe.src.length > 0) {
          console.log(`Video player health check ${checkCount}: OK`);
        }
      } catch (err) {
        console.warn('Video player health check failed:', err);
        // If multiple checks fail, might need to reload
        if (checkCount > 3) {
          console.warn('Video player appears unresponsive, consider reloading');
        }
      }
    };

    // Run health check every 10 seconds
    healthCheckInterval = setInterval(healthCheck, 10000);

    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
    };
  }, [showVideoPlayer, videoEmbedUrl]);

  const StarRating = ({ value, sizeClass = "w-5 h-5" }) => {
    const fullStars = Math.floor(value);
    const hasHalf = value % 1 >= 0.5;

    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => {
          if (i < fullStars) {
            return (
              <svg key={i} className={`${sizeClass} text-white`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            );
          }

          if (i === fullStars && hasHalf) {
            return (
              <svg key={i} className={sizeClass} viewBox="0 0 20 20">
                <defs>
                  <linearGradient id={`half-${i}`}>
                    <stop offset="50%" stopColor="white" />
                    <stop offset="50%" stopColor="#555" />
                  </linearGradient>
                </defs>
                <path
                  fill={`url(#half-${i})`}
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            );
          }

          return (
            <svg key={i} className={`${sizeClass} text-[#555]`} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          );
        })}
      </div>
    );
  };


  
  if (showLoader) {
    return (
      <div className={loading ? '' : 'fade-out-loader'}>
        <FilmStripLoader />
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-dark-bg">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-white text-xl">Film non trouvé</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]">
      <Header />

      {/* Hero Section */}
      <div className="relative h-[70vh] my-12 mx-2 md:mx-10 lg:mx-20">
        {/* Background */}
        <div className="absolute inset-0 rounded-3xl overflow-hidden bg-[#141414] border border-[#262626]">
          <div
            className="absolute inset-0 bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${movie.poster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          {/* Side gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-black/70" />

          {/* Bottom fade */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, #141414 0%, #141414cc 40%, #14141466 65%, #14141400 100%)',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex items-end justify-center pb-24">
          <div className="max-w-3xl text-center">
            <h1 className="text-5xl font-bold text-white mb-4">{movie.title}</h1>

            <p className="text-gray-300 mb-6 line-clamp-3">{movie.description}</p>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              {hasActiveSubscription ? (
                <button
                  onClick={handlePlayMovie}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  <span>Lire maintenant</span>
                </button>
              ) : (
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  S'abonner pour regarder
                </Link>
              )}

              {movie.trailer && (
                <button
                  onClick={() => setShowTrailer(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900/60 text-white rounded-lg hover:bg-gray-900/80 transition font-semibold"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  <span>Voir la bande-annonce</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-8 bg-[#1A1A1A] p-10 rounded-xl border border-[#262626]">
              <h2 className="text-[#999999] text-lg mb-4">Description</h2>
              <p className="text-gray-300">{movie.description}</p>
            </div>

            {/* Cast (as screenshot) */}
            {movie.cast && movie.cast.length > 0 && (
              <div className="relative mb-8 bg-[#1A1A1A] p-8 rounded-2xl border border-[#262626]">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-[#999999] text-lg font-medium">Casting</h2>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => castRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
                      className="w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#262626]"
                      aria-label="Previous"
                    >
                      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => castRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
                      className="w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#262626]"
                      aria-label="Next"
                    >
                      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div
                  ref={castRef}
                  className="flex gap-6 overflow-hidden scroll-smooth select-none"
                  // opsionale: bllokon edhe trackpad/wheel horizontal
                  onWheel={(e) => e.preventDefault()}
                >
                  {movie.cast.map((actor, index) => {
                    const actorName = typeof actor === 'string' ? actor : actor?.name;
                    const actorImage = typeof actor === 'object' ? actor?.image : null;

                    return (
                      <div key={index} className="flex-shrink-0">
                        <div className=" rounded-2xl w-[102px] h-[100px] overflow-hidden bg-[#2A2A2A]">
                          {actorImage ? (
                            <img
                              src={actorImage}
                              alt={actorName}
                              className="w-full h-full object-cover pointer-events-none"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-[#333]" />
                          )}
                        </div>

                        <p className="text-white text-sm font-medium mt-3 truncate text-center">
                          {actorName}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Reviews Slider */}
            {(() => {
              const tmdb = (movie.tmdbReviews || []).map((r, i) => ({
                _key: `tmdb-${i}`,
                name: r.author || 'Anonyme',
                from: r.author_details?.location || 'De Corée', // nëse s’ke location, mbetet fallback
                rating: r.rating ?? r.author_details?.rating ?? null,
                content: r.content || '',
              }));

              const users = (reviews || []).map((r) => ({
                _key: `user-${r._id}`,
                name: r.username || 'Utilisateur',
                from: 'Utilisateur',
                rating: r.rating ?? null,
                content: r.comment || '',
              }));

              const all = [...tmdb, ...users];

              // Mobile: 1 per view, Desktop: 2 per view
              const perView = isMobile ? 1 : 2;
              const totalPages = Math.max(1, Math.ceil(all.length / perView));
              const clampedSlide = Math.min(reviewsSlide, totalPages - 1);

              const prev = () => setReviewsSlide((s) => Math.max(0, s - 1));
              const next = () => setReviewsSlide((s) => Math.min(totalPages - 1, s + 1));

              return (
                <div className="bg-[#1A1A1A] p-10 rounded-2xl border border-[#262626]">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8 flex-col md:flex-row gap-4">
                    <h2 className="text-[#999999] text-lg font-medium">Avis</h2>

                    <button
                      onClick={() => setShowReviewForm(!showReviewForm)}
                      className="inline-flex items-center gap-3 px-6 py-3 bg-black/30 text-white rounded-xl border border-[#2A2A2A] hover:bg-black/50 transition w-full md:w-auto justify-center"
                    >
                      <span className="text-2xl leading-none">+</span>
                      <span className="font-medium">Ajouter votre avis</span>
                    </button>
                  </div>

                  {/* Add review form */}
                  {showReviewForm && (
                    <form onSubmit={handleAddReview} className="mb-10 p-6 bg-[#1A1A1A] rounded-2xl border border-[#262626]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-white mb-2">Note</label>
                          <select
                            value={reviewForm.rating}
                            onChange={(e) =>
                              setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })
                            }
                            className="w-full px-4 py-3 bg-[#101010] text-white rounded-xl border border-[#262626]"
                          >
                            {[1, 2, 3, 4, 5].map((r) => (
                              <option key={r} value={r}>
                                {r} Étoile{r > 1 ? 's' : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-white mb-2">Commentaire</label>
                          <textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                            className="w-full px-4 py-3 bg-[#101010] text-white rounded-xl border border-[#262626]"
                            rows="4"
                            required
                          />
                        </div>

                        <div className="md:col-span-2">
                          <button
                            type="submit"
                            className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition font-semibold"
                          >
                            Soumettre l'avis
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Slider */}
                  {all.length > 0 ? (
                    <div className="relative">
                      {/* viewport */}
                      <div className="overflow-hidden">
                        <div
                          className="flex transition-transform duration-500 ease-out"
                          style={{ transform: `translateX(-${clampedSlide * 100}%)` }}
                        >
                          {/* Pages */}
                          {Array.from({ length: totalPages }).map((_, pageIdx) => {
                            const start = pageIdx * perView;
                            const pageItems = all.slice(start, start + perView);

                            return (
                              <div key={pageIdx} className="w-full flex-shrink-0">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  {pageItems.map((it) => (
                                    <div
                                      key={it._key}
                                      className="bg-[#0F0F0F] rounded-2xl border border-[#262626] p-8 min-h-[260px]"
                                    >
                                      <div className="flex items-start justify-between gap-4 mb-6 flex-col sm:flex-row">
                                        <div>
                                          <div className="text-white text-2xl font-semibold">{it.name}</div>
                                          <div className="text-[#7C7C7C] mt-1">{it.from}</div>
                                        </div>

                                        {it.rating != null && (
                                          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 border border-[#262626] shrink-0">
                                            <div className="flex items-center">
                                              {Array.from({ length: 5 }).map((__, sIdx) => (
                                                <svg
                                                  key={sIdx}
                                                  className={`w-4 h-4 ${sIdx < Math.round(it.rating) ? 'text-white' : 'text-white/25'}`}
                                                  fill="currentColor"
                                                  viewBox="0 0 20 20"
                                                >
                                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                              ))}
                                            </div>
                                            <span className="text-white/80">{Number(it.rating).toFixed(1).replace('.0','')}</span>
                                          </div>
                                        )}
                                      </div>

                                      <p className="text-[#9A9A9A] leading-relaxed line-clamp-5">
                                        {it.content}
                                      </p>
                                    </div>
                                  ))}

                                  {/* nëse faqja e fundit ka vetëm 1 item, mbush "placeholder" që layout të rrijë si foto */}
                                  {pageItems.length === 1 && (
                                    <div className="hidden md:block bg-transparent" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Bottom controls (arrows + dots) */}
                      {totalPages > 1 && (
                        <div className="mt-10 flex items-center justify-center gap-6">
                          <button
                            type="button"
                            onClick={prev}
                            disabled={clampedSlide === 0}
                            className={`w-12 h-12 rounded-full border border-[#262626] flex items-center justify-center transition
                              ${clampedSlide === 0 ? 'bg-black/20 text-white/30' : 'bg-black/35 hover:bg-black/55 text-white'}`}
                            aria-label="Previous"
                          >
                            <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>

                          <div className="flex items-center gap-2">
                            {Array.from({ length: totalPages }).map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setReviewsSlide(idx)}
                                className={`h-1.5 rounded-full transition ${
                                  idx === clampedSlide ? 'bg-white w-10' : 'bg-white/20 w-5'
                                }`}
                                aria-label={`Go to page ${idx + 1}`}
                                type="button"
                              />
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={next}
                            disabled={clampedSlide === totalPages - 1}
                            className={`w-12 h-12 rounded-full border border-[#262626] flex items-center justify-center transition
                              ${clampedSlide === totalPages - 1 ? 'bg-black/20 text-white/30' : 'bg-black/35 hover:bg-black/55 text-white'}`}
                            aria-label="Next"
                          >
                            <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-400">Aucun avis pour le moment. Soyez le premier à donner votre avis !</p>
                  )}
                </div>
              );
            })()}

          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#1A1A1A] p-10 rounded-xl border border-[#262626] space-y-6">
              <div>
                <div className='flex items-center gap-2 mb-2'>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.75 3V5.25M17.25 3V5.25M3 18.75V7.5C3 6.25736 4.00736 5.25 5.25 5.25H18.75C19.9926 5.25 21 6.25736 21 7.5V18.75M3 18.75C3 19.9926 4.00736 21 5.25 21H18.75C19.9926 21 21 19.9926 21 18.75M3 18.75V11.25C3 10.0074 4.00736 9 5.25 9H18.75C19.9926 9 21 10.0074 21 11.25V18.75" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  
                  <h3 className="text-[#999999] font-medium">Année de sortie</h3>                  
                </div>
                <p className="text-gray-300">{movie.releaseYear}</p>
              </div>

              <div className='flex items-start flex-col gap-2'>
                <div className='flex items-center gap-2 mb-2'>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.5 21L15.75 9.75L21 21M12 18H19.5M3 5.62136C4.96557 5.37626 6.96804 5.25 9 5.25M9 5.25C10.1208 5.25 11.2326 5.28841 12.3343 5.364M9 5.25V3M12.3343 5.364C11.1763 10.6578 7.68868 15.0801 3 17.5023M12.3343 5.364C13.2298 5.42545 14.1186 5.51146 15 5.62136M10.4113 14.1162C8.78554 12.4619 7.47704 10.4949 6.58432 8.31366" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <h3 className="text-[#999999] font-medium">Langues disponibles</h3>                  
                </div>
                <p className="text-white bg-[#141414] px-3 py-1 rounded-lg border border-[#262626]">{movie.language?.join(', ') || 'English'}</p>
              </div>
              <div>
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M11.4807 3.4987C11.6729 3.03673 12.3273 3.03673 12.5195 3.4987L14.6454 8.61016C14.7264 8.80492 14.9096 8.93799 15.1199 8.95485L20.6381 9.39724C21.1369 9.43722 21.3391 10.0596 20.9591 10.3851L16.7548 13.9866C16.5946 14.1238 16.5246 14.3391 16.5736 14.5443L17.858 19.9292C17.9741 20.4159 17.4447 20.8005 17.0177 20.5397L12.2933 17.6541C12.1133 17.5441 11.8869 17.5441 11.7069 17.6541L6.98251 20.5397C6.55551 20.8005 6.02606 20.4159 6.14215 19.9292L7.42664 14.5443C7.47558 14.3391 7.40562 14.1238 7.24543 13.9866L3.04111 10.3851C2.66112 10.0596 2.86335 9.43722 3.36209 9.39724L8.88034 8.95485C9.0906 8.93799 9.27375 8.80492 9.35476 8.61016L11.4807 3.4987Z"
                      stroke="#999999"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <h3 className="text-[#999999]  font-medium">Notes</h3>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {/* H4KIG */}
                  <div className="bg-[#0F0F0F] border border-[#262626] rounded-2xl p-4">
                    <div className="text-white text-xl font-semibold mb-3 truncate">H4KIG STREAM</div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-shrink-0">
                        <StarRating value={movie.rating?.site || 0} sizeClass="w-6 h-6" />
                      </div>
                      <span className="text-white text-xl font-semibold shrink-0 leading-none whitespace-nowrap">
                        {(movie.rating?.site || 0).toFixed(1).replace('.0', '')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>



              <div>
                <div className='flex items-center gap-2 mb-4'>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.75 3C0.75 1.75736 1.75736 0.75 3 0.75H5.25C6.49264 0.75 7.5 1.75736 7.5 3V5.25C7.5 6.49264 6.49264 7.5 5.25 7.5H3C1.75736 7.5 0.75 6.49264 0.75 5.25V3Z" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M0.75 12.75C0.75 11.5074 1.75736 10.5 3 10.5H5.25C6.49264 10.5 7.5 11.5074 7.5 12.75V15C7.5 16.2426 6.49264 17.25 5.25 17.25H3C1.75736 17.25 0.75 16.2426 0.75 15V12.75Z" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10.5 3C10.5 1.75736 11.5074 0.75 12.75 0.75H15C16.2426 0.75 17.25 1.75736 17.25 3V5.25C17.25 6.49264 16.2426 7.5 15 7.5H12.75C11.5074 7.5 10.5 6.49264 10.5 5.25V3Z" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10.5 12.75C10.5 11.5074 11.5074 10.5 12.75 10.5H15C16.2426 10.5 17.25 11.5074 17.25 12.75V15C17.25 16.2426 16.2426 17.25 15 17.25H12.75C11.5074 17.25 10.5 16.2426 10.5 15V12.75Z" stroke="#999999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <h3 className="font-medium text-[#999999]">Genres</h3>                  
                </div>
                <div className="flex flex-wrap gap-2">
                  {movie.genre?.map((g, i) => (
                    <span key={i} className="px-3 py-1 border border-[#262626] bg-[#141414] text-white rounded text-sm">
                      {g}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-[#999999] mb-2">Réalisateur</h3>
                <p className="text-gray-300">{movie.director}</p>
              </div>           
            </div>
          </div>
        </div>
      </div>

      {/* Trailer Modal */}
      {showTrailer && movie?.trailer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowTrailer(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-gray-300 transition z-10 font-bold"
              style={{ fontSize: '2rem', zIndex: 10000 }}
            >
              ✕
            </button>
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getYouTubeEmbedUrl(movie.trailer)}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                playsInline
                webkit-playsinline="true"
                loading="eager"
                title="Trailer"
                style={{ touchAction: 'none' }}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoPlayer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center p-4"
          onClick={() => {
            // Exit fullscreen if active
            if (document.fullscreenElement || 
                document.webkitFullscreenElement || 
                document.mozFullScreenElement || 
                document.msFullscreenElement) {
              if (document.exitFullscreen) {
                document.exitFullscreen();
              } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
              } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
              } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
              }
            }
            // Clean up
            setShowVideoPlayer(false);
            setVideoEmbedUrl('');
            setVideoError(null);
            setLoadingVideo(false);
          }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                // Exit fullscreen if active
                if (document.fullscreenElement || 
                    document.webkitFullscreenElement || 
                    document.mozFullScreenElement || 
                    document.msFullscreenElement) {
                  if (document.exitFullscreen) {
                    document.exitFullscreen();
                  } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                  } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                  } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                  }
                }
                // Clean up
                setShowVideoPlayer(false);
                setVideoEmbedUrl('');
                setVideoError(null);
                setLoadingVideo(false);
              }}
              className="absolute -top-12 right-0 text-white text-3xl hover:text-gray-300 transition z-10 font-bold"
              style={{ fontSize: '2rem', zIndex: 10000 }}
            >
              ✕
            </button>

            {loadingVideo ? (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-xl">Loading video...</div>
                </div>
              </div>
            ) : videoEmbedUrl ? (
              <div 
                ref={videoContainerRef}
                className="relative" 
                style={{ paddingBottom: '56.25%' }}
              >
                <iframe
                  ref={videoIframeRef}
                  key={videoEmbedUrl} // Force reload if URL changes
                  src={videoEmbedUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  playsInline
                  webkit-playsinline="true"
                  loading="eager"
                  title="Movie Player"
                  style={{ touchAction: 'none' }}
                  onLoad={() => {
                    console.log('Video player iframe loaded successfully');
                    setVideoError(null);
                    // Ensure video is playing
                    try {
                      const iframe = videoIframeRef.current;
                      if (iframe && iframe.contentWindow) {
                        // Video should autoplay from Bunny Stream embed URL
                        console.log('Iframe content window accessible');
                      }
                    } catch (err) {
                      console.log('Cannot access iframe content (expected due to CORS):', err);
                    }
                  }}
                  onError={(e) => {
                    console.error('Video player iframe error:', e);
                    setVideoError('Échec du chargement du lecteur vidéo. Veuillez réessayer.');
                  }}
                />
              </div>
            ) : videoError ? (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <div className="text-white text-xl text-center px-4">{videoError}</div>
                  <button
                    onClick={handlePlayMovie}
                    className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-xl">Échec du chargement de la vidéo</div>
                </div>
              </div>
            )}
          </div>
          
        </div>
      )}
        <div className="container mx-auto px-4 mb-12">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={trial}
              alt="Free trial background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 " />
            <div className="relative z-10 p-8 md:p-20 ">
                <div className='flex items-center justify-between flex-col md:flex-row'>
                    <div className='flex flex-col items-start'>
                      <h2 className="text-center md:text-start text-3xl md:text-4xl font-semibold text-white mb-4">
                        Des milliers de films et séries, dès maintenant !
                      </h2>

                      <p className="text-gray-300 mb-6 text-center md:text-start">
                        Accès illimité au catalogue complet en Full HD 1080p, sans publicité.<br />
                        Compatible TV, PC, Mobile et Mac.
                      </p>                
                    </div>
                    <Link
                      to="/subscriptions"
                      className="inline-flex items-center justify-center px-6 py-3
                                bg-white text-gray-900 rounded-xl
                                hover:bg-gray-100 transition font-semibold"
                    >
                      Accès immédiat
                    </Link>                  
                </div>
            </div>

          </div>
        </div>     


      <Footer />
    </div>
  );
};

export default MovieDetail;
